import { useState, useMemo } from 'react';
import { OpsLayout } from '@/components/ops/OpsLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Plus, 
  MoreHorizontal, 
  Edit, 
  Power, 
  PowerOff, 
  History,
  Users,
  Search,
  Filter,
  X,
  AlertCircle,
} from 'lucide-react';
import { 
  useAllSurgeonSubsets,
  useCreateSurgeonSubset,
  useUpdateSurgeonSubset,
  useDeactivateSurgeonSubset,
  useReactivateSurgeonSubset,
} from '@/hooks/useSurgeonSubsets';
import { 
  SurgeonSubsetTemplate,
  PayerScopeType,
  PAYER_SCOPE_LABELS,
  SubsetFormState,
  initialSubsetFormState,
} from '@/lib/authorization/subsetTypes';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';

// Fetch surgeons from profiles with surgeon role
function useSurgeons() {
  return useQuery({
    queryKey: ['surgeons-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          user_id,
          full_name,
          email
        `)
        .order('full_name');
      
      if (error) throw error;
      return data || [];
    },
  });
}

// Fetch CPT codes for selection
function useCptCodes() {
  return useQuery({
    queryKey: ['cpt-codes-list'],
    queryFn: async () => {
      const { data: raw, error } = await supabase
        .from('cpt_catalog')
        .select('cpt, descriptor, category')
        .order('cpt');
      const data = (raw || []).map(r => ({ code: r.cpt, description: r.descriptor, category: r.category }));
      
      if (error) throw error;
      return data || [];
    },
  });
}

// Fetch payers for specific payer selection
function usePayers() {
  return useQuery({
    queryKey: ['payers-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payers')
        .select('id, name')
        .order('name');
      
      if (error) throw error;
      return data || [];
    },
  });
}

export default function SurgeonSubsetsAdmin() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSubset, setEditingSubset] = useState<SurgeonSubsetTemplate | null>(null);
  const [formState, setFormState] = useState<SubsetFormState>(initialSubsetFormState);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('active');
  const [selectedCptToAdd, setSelectedCptToAdd] = useState('');

  // Data queries
  const { data: subsets, isLoading: subsetsLoading } = useAllSurgeonSubsets();
  const { data: surgeons, isLoading: surgeonsLoading } = useSurgeons();
  const { data: cptCodes, isLoading: cptLoading } = useCptCodes();
  const { data: payers } = usePayers();

  // Mutations
  const createMutation = useCreateSurgeonSubset();
  const updateMutation = useUpdateSurgeonSubset();
  const deactivateMutation = useDeactivateSurgeonSubset();
  const reactivateMutation = useReactivateSurgeonSubset();

  // Build surgeon lookup
  const surgeonMap = useMemo(() => {
    const map = new Map<string, { name: string; email: string }>();
    surgeons?.forEach(s => {
      map.set(s.user_id, { name: s.full_name || 'Unknown', email: s.email || '' });
    });
    return map;
  }, [surgeons]);

  // Build CPT lookup
  const cptMap = useMemo(() => {
    const map = new Map<string, string>();
    cptCodes?.forEach(c => {
      map.set(c.code, c.description);
    });
    return map;
  }, [cptCodes]);

  // Build payer lookup
  const payerMap = useMemo(() => {
    const map = new Map<string, string>();
    payers?.forEach(p => {
      map.set(p.id, p.name);
    });
    return map;
  }, [payers]);

  // Filter subsets
  const filteredSubsets = useMemo(() => {
    if (!subsets) return [];
    
    return subsets.filter(s => {
      // Filter by active status
      if (filterActive === 'active' && !s.is_active) return false;
      if (filterActive === 'inactive' && s.is_active) return false;
      
      // Filter out superseded versions (unless viewing history)
      if (s.superseded_by) return false;
      
      // Search filter
      if (searchQuery) {
        const surgeonName = surgeonMap.get(s.surgeon_id)?.name || '';
        const searchLower = searchQuery.toLowerCase();
        return (
          surgeonName.toLowerCase().includes(searchLower) ||
          s.primary_cpt.includes(searchQuery) ||
          s.subset_cpts.some(cpt => cpt.includes(searchQuery))
        );
      }
      
      return true;
    });
  }, [subsets, filterActive, searchQuery, surgeonMap]);

  const handleOpenCreate = () => {
    setEditingSubset(null);
    setFormState(initialSubsetFormState);
    setIsDialogOpen(true);
  };

  const handleOpenEdit = (subset: SurgeonSubsetTemplate) => {
    setEditingSubset(subset);
    setFormState({
      surgeon_id: subset.surgeon_id,
      primary_cpt: subset.primary_cpt,
      subset_cpts: [...subset.subset_cpts],
      payer_scope: subset.payer_scope as PayerScopeType,
      specific_payer_id: subset.specific_payer_id,
      notes: subset.notes || '',
    });
    setIsDialogOpen(true);
  };

  const handleAddSubsetCpt = () => {
    if (selectedCptToAdd && !formState.subset_cpts.includes(selectedCptToAdd)) {
      setFormState(prev => ({
        ...prev,
        subset_cpts: [...prev.subset_cpts, selectedCptToAdd],
      }));
      setSelectedCptToAdd('');
    }
  };

  const handleRemoveSubsetCpt = (cpt: string) => {
    setFormState(prev => ({
      ...prev,
      subset_cpts: prev.subset_cpts.filter(c => c !== cpt),
    }));
  };

  const handleSubmit = async () => {
    if (!formState.surgeon_id || !formState.primary_cpt || formState.subset_cpts.length === 0) {
      return;
    }

    if (editingSubset) {
      // Update creates a new version
      await updateMutation.mutateAsync({
        id: editingSubset.id,
        updates: {
          subset_cpts: formState.subset_cpts,
          payer_scope: formState.payer_scope,
          specific_payer_id: formState.payer_scope === 'specific' ? formState.specific_payer_id : null,
          notes: formState.notes || undefined,
        },
      });
    } else {
      await createMutation.mutateAsync({
        surgeon_id: formState.surgeon_id,
        primary_cpt: formState.primary_cpt,
        subset_cpts: formState.subset_cpts,
        payer_scope: formState.payer_scope,
        specific_payer_id: formState.payer_scope === 'specific' ? formState.specific_payer_id : null,
        notes: formState.notes || undefined,
      });
    }

    setIsDialogOpen(false);
  };

  const isLoading = subsetsLoading || surgeonsLoading || cptLoading;
  const isMutating = createMutation.isPending || updateMutation.isPending;

  return (
    <OpsLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Users className="w-6 h-6 text-primary" />
              Surgeon Authorization Subsets
            </h1>
            <p className="text-muted-foreground mt-1">
              Define surgeon-specific ancillary procedure bundles for authorization requests
            </p>
          </div>
          <Button onClick={handleOpenCreate}>
            <Plus className="w-4 h-4 mr-2" />
            New Subset Template
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by surgeon or CPT..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <Select value={filterActive} onValueChange={(v) => setFilterActive(v as typeof filterActive)}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subsets Table */}
        <Card>
          <CardHeader>
            <CardTitle>Subset Templates</CardTitle>
            <CardDescription>
              {filteredSubsets.length} template{filteredSubsets.length !== 1 ? 's' : ''} found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : filteredSubsets.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No subset templates found</p>
                <p className="text-sm">Create your first template to get started</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Surgeon</TableHead>
                    <TableHead>Primary CPT</TableHead>
                    <TableHead>Subset CPTs</TableHead>
                    <TableHead>Payer Scope</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSubsets.map((subset) => {
                    const surgeon = surgeonMap.get(subset.surgeon_id);
                    const payerName = subset.specific_payer_id 
                      ? payerMap.get(subset.specific_payer_id) 
                      : null;
                    
                    return (
                      <TableRow key={subset.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{surgeon?.name || 'Unknown'}</div>
                            <div className="text-xs text-muted-foreground">{surgeon?.email}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <code className="font-mono font-medium">{subset.primary_cpt}</code>
                            <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                              {cptMap.get(subset.primary_cpt) || ''}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-[200px]">
                            {subset.subset_cpts.map(cpt => (
                              <Badge key={cpt} variant="outline" className="font-mono text-xs">
                                {cpt}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {PAYER_SCOPE_LABELS[subset.payer_scope as PayerScopeType]}
                          </Badge>
                          {payerName && (
                            <div className="text-xs text-muted-foreground mt-1">{payerName}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">v{subset.version}</Badge>
                        </TableCell>
                        <TableCell>
                          {subset.is_active ? (
                            <Badge className="bg-success/20 text-success border-success/30">
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="secondary">Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {format(new Date(subset.created_at), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleOpenEdit(subset)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Edit (New Version)
                              </DropdownMenuItem>
                              {subset.is_active ? (
                                <DropdownMenuItem 
                                  onClick={() => deactivateMutation.mutate(subset.id)}
                                  className="text-destructive"
                                >
                                  <PowerOff className="w-4 h-4 mr-2" />
                                  Deactivate
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => reactivateMutation.mutate(subset.id)}>
                                  <Power className="w-4 h-4 mr-2" />
                                  Reactivate
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingSubset ? 'Edit Subset Template' : 'Create Subset Template'}
              </DialogTitle>
              <DialogDescription>
                {editingSubset 
                  ? 'Editing will create a new version. The previous version will be preserved.'
                  : 'Define ancillary CPT codes for a surgeon + primary procedure combination.'
                }
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Surgeon Selection */}
              <div className="space-y-2">
                <Label>Surgeon</Label>
                <Select
                  value={formState.surgeon_id}
                  onValueChange={(v) => setFormState(prev => ({ ...prev, surgeon_id: v }))}
                  disabled={!!editingSubset}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select surgeon" />
                  </SelectTrigger>
                  <SelectContent>
                    {surgeons?.map(s => (
                      <SelectItem key={s.user_id} value={s.user_id}>
                        {s.full_name || s.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Primary CPT */}
              <div className="space-y-2">
                <Label>Primary CPT Code</Label>
                <Select
                  value={formState.primary_cpt}
                  onValueChange={(v) => setFormState(prev => ({ ...prev, primary_cpt: v }))}
                  disabled={!!editingSubset}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select primary CPT" />
                  </SelectTrigger>
                  <SelectContent>
                    {cptCodes?.map(c => (
                      <SelectItem key={c.code} value={c.code}>
                        <span className="font-mono">{c.code}</span>
                        <span className="text-muted-foreground ml-2 text-xs truncate">
                          {c.description}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Subset CPTs */}
              <div className="space-y-2">
                <Label>Subset CPT Codes (Ancillary Procedures)</Label>
                <div className="flex gap-2">
                  <Select value={selectedCptToAdd} onValueChange={setSelectedCptToAdd}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Add CPT code" />
                    </SelectTrigger>
                    <SelectContent>
                      {cptCodes
                        ?.filter(c => c.code !== formState.primary_cpt && !formState.subset_cpts.includes(c.code))
                        .map(c => (
                          <SelectItem key={c.code} value={c.code}>
                            <span className="font-mono">{c.code}</span>
                            <span className="text-muted-foreground ml-2 text-xs">
                              {c.description}
                            </span>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="secondary" onClick={handleAddSubsetCpt}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                
                {formState.subset_cpts.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formState.subset_cpts.map(cpt => (
                      <Badge 
                        key={cpt} 
                        variant="secondary" 
                        className="font-mono pl-2 pr-1 py-1 flex items-center gap-1"
                      >
                        {cpt}
                        <button
                          type="button"
                          onClick={() => handleRemoveSubsetCpt(cpt)}
                          className="hover:bg-destructive/20 rounded p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}

                {formState.subset_cpts.length === 0 && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    At least one subset CPT is required
                  </p>
                )}
              </div>

              {/* Payer Scope */}
              <div className="space-y-2">
                <Label>Payer Scope</Label>
                <Select
                  value={formState.payer_scope}
                  onValueChange={(v) => setFormState(prev => ({ 
                    ...prev, 
                    payer_scope: v as PayerScopeType,
                    specific_payer_id: v !== 'specific' ? null : prev.specific_payer_id,
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PAYER_SCOPE_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Specific Payer (conditional) */}
              {formState.payer_scope === 'specific' && (
                <div className="space-y-2">
                  <Label>Specific Payer</Label>
                  <Select
                    value={formState.specific_payer_id || ''}
                    onValueChange={(v) => setFormState(prev => ({ ...prev, specific_payer_id: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select payer" />
                    </SelectTrigger>
                    <SelectContent>
                      {payers?.map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={formState.notes}
                  onChange={(e) => setFormState(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Add any notes about this subset template..."
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={
                  isMutating || 
                  !formState.surgeon_id || 
                  !formState.primary_cpt || 
                  formState.subset_cpts.length === 0 ||
                  (formState.payer_scope === 'specific' && !formState.specific_payer_id)
                }
              >
                {isMutating ? 'Saving...' : (editingSubset ? 'Save New Version' : 'Create Template')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </OpsLayout>
  );
}
